<script setup>
/**
 *  Usage:
 *
 *  <card-tabbed>
 *      <card-tabbed-tab
 *          title="Tab One"
 *          icon="fa-regular fa-circle text-myflowGreen"
 *          value="tab-one"
 *          :active="true"
 *      >
 *          <h2>Tab one</h2>
 *          <p>Lorem ipsum</p>
 *      </card-tabbed-tab>
 *
 *      <card-tabbed-tab
 *          title="Tab Two"
 *          icon="fa-regular fa-circle text-myflowRed"
 *          value="tab-two"
 *          :active="false"
 *      >
 *          <h2>Tab two</h2>
 *          <p>Lorem ipsum again</p>
 *      </card-tabbed-tab>
 *  </card-tabbed>
 */

import {ref, provide, watchEffect, useSlots, onMounted, watch, computed} from 'vue'
import TabList from './_tabList.vue'

const props = defineProps({
    nametab: {
        type: String
    },
    classname: {
        type: String,
        default: null
    },
    trackState: {
        type: Boolean,
        required: false,
        default: true,
    },
    preselectedTab: {
        type: String,
        required: false,
        default: null,
    }
})

const emit = defineEmits(['changestatustab'])
const slots = useSlots();

const formIsFilled = ref(false)
const tabTitles = computed(() => {
    return slots.default().flatMap((tab) => tab.props !== null ? tab.props : [])
})
const isSelected = ref(Object.fromEntries(Object.entries(tabTitles.value).filter(([key, value]) => value.active === true )))
const selectedId = Object.keys(isSelected.value).length ? Object.keys(isSelected.value)[0] : null
const selectedTitle = ref(tabTitles.value[selectedId].value)

provide('selectedTitle', selectedTitle)

watchEffect(() => {
    if(selectedTitle.value && formIsFilled.value) {
        emit('changestatustab', selectedTitle.value)
    }
    formIsFilled.value = true
})

const selectTab = (tab) => {
    selectedTitle.value = tab
}

watch(selectedTitle, (newValue) => {
    if (! props.trackState) {
        return
    }
    if (newValue) {
        window.location.hash = newValue
    }
})

defineExpose({
    selectTab
})

onMounted(() => {
    const presetTab = window.location.hash.replace('#', '')

    if (presetTab && tabTitles.value.find(tab => tab.value === presetTab)) {
        selectedTitle.value = presetTab
    } else if (props.preselectedTab) {
        selectedTitle.value = props.preselectedTab
    }
})

</script>
<template>
    <div class="bg-white border border-gray-200 rounded-xl @container w-full max-w-screen">
        <tab-list
            v-bind:id="nametab"
            v-bind:class="classname"
            class="overflow-x-auto rounded-t-xl bg-gray-50 pt-1.5"
            :tabs="tabTitles"
            :selected="selectedTitle"
            @select="selectedTitle = $event"
        />
        <div class="p-4">
            <slot/>
        </div>
    </div>
</template>
